/**
 * Generates a fake waveform with a deterministic pattern
 * @param bars - Number of bars to generate (default: 60)
 * @returns Array of bar heights in pixels
 */
function generateFakeWaveform(bars: number = 60): number[] {
  return Array.from({ length: bars }, (_, i) => {
    const baseHeight = 15;
    const variation = Math.sin((i / bars) * Math.PI * 4) * 20;
    const random = (i * 17) % 15;
    return Math.max(8, baseHeight + variation + random);
  });
}

/**
 * Generates waveform data from an audio URL
 * @param audioUrl - URL of the audio file
 * @param bars - Number of bars to generate (default: 60)
 * @param isStream - If true, returns fake waveform without fetching audio (default: false)
 * @returns Promise that resolves to an array of bar heights in pixels
 */
export async function generateWaveform(
  audioUrl: string,
  bars: number = 60,
  isStream: boolean = false,
): Promise<number[]> {
  try {
    // For streams, return fake waveform (more efficient)
    if (isStream) {
      if (import.meta.env.DEV) {
        console.log('[Waveform] Using fake waveform for stream:', audioUrl);
      }
      return generateFakeWaveform(bars);
    }

    if (import.meta.env.DEV) {
      console.log('[Waveform] Starting generation for:', audioUrl);
    }

    const response = await fetch(audioUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch audio: ${response.status}`);
    }

    const arrayBuffer = await response.arrayBuffer();
    const audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const channelData = audioBuffer.getChannelData(0);
    const samples = channelData.length;
    const blockSize = Math.floor(samples / bars);
    const waveform: number[] = [];

    if (import.meta.env.DEV) {
      console.log(
        '[Waveform] Audio samples:',
        samples,
        'Block size:',
        blockSize,
      );
    }

    const peaks: number[] = [];
    for (let i = 0; i < bars; i++) {
      const start = i * blockSize;
      const end = Math.min(start + blockSize, samples);
      let max = 0;
      let sumSquares = 0;
      let count = 0;
      for (let j = start; j < end; j++) {
        const abs = Math.abs(channelData[j]);
        if (abs > max) max = abs;
        sumSquares += channelData[j] * channelData[j];
        count++;
      }
      const rms = count > 0 ? Math.sqrt(sumSquares / count) : 0;
      const combined = rms * 0.8 + max * 0.2;
      peaks.push(combined);
    }

    const sortedPeaks = [...peaks].sort((a, b) => a - b);
    const p95 = sortedPeaks[Math.floor(sortedPeaks.length * 0.95)] || 0.001;
    const p50 = sortedPeaks[Math.floor(sortedPeaks.length * 0.5)] || 0.001;
    const p10 = sortedPeaks[Math.floor(sortedPeaks.length * 0.1)] || 0.001;

    const minHeight = 6;
    const maxHeight = 48;
    for (let i = 0; i < peaks.length; i++) {
      const peak = peaks[i];
      let normalized: number;

      if (peak >= p95) {
        normalized = 0.8 + ((peak - p95) / (p95 - p50 + 0.001)) * 0.2;
      } else if (peak >= p50) {
        normalized = 0.3 + ((peak - p50) / (p95 - p50 + 0.001)) * 0.5;
      } else if (peak >= p10) {
        normalized = 0.15 + ((peak - p10) / (p50 - p10 + 0.001)) * 0.15;
      } else {
        normalized = 0.125 + ((peak - 0) / (p10 + 0.001)) * 0.025;
      }

      const logScaled = Math.log10(normalized * 9 + 1) / Math.log10(10);
      const height = minHeight + logScaled * (maxHeight - minHeight);
      waveform.push(Math.max(minHeight, Math.min(maxHeight, height)));
    }

    if (import.meta.env.DEV) {
      console.log(
        '[Waveform] Generated waveform data:',
        waveform.length,
        'bars',
      );
    }

    audioContext.close();
    return waveform;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('[Waveform] Error generating waveform:', error);
    }
    throw error;
  }
}
